import { __assign, __makeTemplateObject, __read, __rest } from "tslib";
import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef, useLayoutEffect, } from 'react';
import { runKeyHandler } from '@material-ui/pickers/_shared/hooks/useKeyDown';
import { ArrowDown as ArrowDownIcon, ArrowUp as ArrowUpIcon, } from '@ringcentral/juno-icon';
import { styled, useChange, useDepsChange, useEventCallback, useForceUpdate, useRefState, } from '../../../../foundation';
import { StyledNumberPicker } from './styles/StyledNumberPicker';
import { StyledTimeIconButton } from './styles/StyledTimeIconButton';
import { pad } from './utils';
var _NumberPicker = forwardRef(function (props, ref) {
    var onClick = props.onClick, value = props.value, onUpdateValue = props.onUpdateValue, onKeyDown = props.onKeyDown, source = props.source, automationId = props.automationId, size = props.size, minProp = props.min, maxProp = props.max, onClose = props.onClose, step = props.step, onInnerChange = props.onInnerChange, renderValue = props.renderValue, getScreenReaderLabel = props.getScreenReaderLabel, autoFocus = props.autoFocus, rest = __rest(props, ["onClick", "value", "onUpdateValue", "onKeyDown", "source", "automationId", "size", "min", "max", "onClose", "step", "onInnerChange", "renderValue", "getScreenReaderLabel", "autoFocus"]);
    var forceUpdate = useForceUpdate();
    var _a = __read(useRefState(value, forceUpdate), 2), innerValueRef = _a[0], setInnerValue = _a[1];
    var rangeRef = useRef({ max: maxProp, min: minProp });
    var pickerRef = useRef(null);
    useLayoutEffect(function () {
        if (autoFocus) {
            pickerRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useDepsChange(function () {
        rangeRef.current = { max: maxProp, min: minProp };
    }, [maxProp, minProp]);
    useChange(function () {
        if (innerValueRef.current !== value) {
            setInnerValue(value, false);
        }
    }, function () { return value; });
    var innerValue = innerValueRef.current;
    var index = Math.floor(innerValue / step);
    var isFirst = index === 0;
    var isLatest = index === source.length - 1;
    var firstValue = source[0];
    var latestValue = source[source.length - 1];
    var decreaseValue = isFirst ? latestValue : source[index - 1];
    var increaseValue = isLatest ? firstValue : source[index + 1];
    var _b = rangeRef.current, min = _b.min, max = _b.max;
    var decreaseDisabled = isFirst
        ? !!(max !== undefined && latestValue > max)
        : !!(min !== undefined && decreaseValue < min);
    var increaseDisabled = isLatest
        ? !!(min !== undefined && firstValue < min)
        : !!(max !== undefined && increaseValue > max);
    var showValue = useMemo(function () {
        if (renderValue) {
            return renderValue(innerValue);
        }
        return pad(innerValue);
    }, [innerValue, renderValue]);
    var label = getScreenReaderLabel === null || getScreenReaderLabel === void 0 ? void 0 : getScreenReaderLabel(showValue);
    var handleKeyDown = useEventCallback(function (event) {
        var keydownSubmit = function () {
            event.stopPropagation();
            onUpdateValue(innerValueRef.current);
            onClose(event);
        };
        var handleInnerValueChange = function (toValue) {
            setInnerValue(toValue);
            onInnerChange === null || onInnerChange === void 0 ? void 0 : onInnerChange(toValue);
        };
        runKeyHandler(event, {
            ArrowUp: function () {
                if (increaseDisabled) {
                    return;
                }
                handleInnerValueChange(increaseValue);
            },
            ArrowDown: function () {
                if (decreaseDisabled) {
                    return;
                }
                handleInnerValueChange(decreaseValue);
            },
            Enter: keydownSubmit,
            ' ': keydownSubmit,
        });
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
    });
    useImperativeHandle(ref, function () { return ({
        value: innerValue,
        setRange: function (newRange) {
            var range = rangeRef.current;
            if (range.max !== newRange.max || range.min !== newRange.min) {
                var currentValue = -1;
                if (newRange.max !== undefined &&
                    innerValueRef.current > newRange.max) {
                    currentValue = newRange.max;
                }
                if (newRange.min !== undefined &&
                    innerValueRef.current < newRange.min) {
                    currentValue = newRange.min;
                }
                rangeRef.current = newRange;
                if (currentValue !== -1) {
                    setInnerValue(currentValue, false);
                }
                forceUpdate();
            }
        },
    }); }, [forceUpdate, innerValue, innerValueRef, setInnerValue]);
    var iconSize = (function () {
        switch (size) {
            case 'small':
                return 'medium';
            case 'medium':
            default:
                return 'large';
        }
    })();
    return (React.createElement(StyledNumberPicker, __assign({ tabIndex: 0, onKeyDown: handleKeyDown, "aria-live": "assertive", "aria-label": label, ref: pickerRef, "data-focusable": true, role: "spinbutton" }, rest),
        React.createElement(StyledTimeIconButton, { tabIndex: -1, size: iconSize, color: "neutral.f04", wrapperSize: size, disabled: increaseDisabled, onClick: function () {
                onUpdateValue(increaseValue);
            }, symbol: ArrowUpIcon, "data-test-automation-id": automationId && automationId + "-prev-pagination", "aria-label": "Arrow Up" }),
        React.createElement(StyledTimeIconButton, { wrapperSize: size, tabIndex: -1, onClick: onClick, "data-test-automation-id": automationId && automationId + "-text" },
            React.createElement(React.Fragment, null, 
            // TODO: that <></> will fix when `RcIconButton` ready
            showValue)),
        React.createElement(StyledTimeIconButton, { tabIndex: -1, size: iconSize, color: "neutral.f04", wrapperSize: size, disabled: decreaseDisabled, onClick: function () {
                onUpdateValue(decreaseValue);
            }, symbol: ArrowDownIcon, "data-test-automation-id": automationId && automationId + "-next-pagination", "aria-label": "Arrow Down" })));
});
var NumberPicker = styled(memo(_NumberPicker))(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""])));
NumberPicker.displayName = 'NumberPicker';
export { NumberPicker };
var templateObject_1;
